import { CalendarDaysIcon } from "@heroicons/react/20/solid";
import { useState, useEffect } from "react";

export type YearRange = { yearFrom: string; yearTo: string };

type YearFilterProps = {
  yearFrom: string;
  yearTo: string;
  setYearRange: (range: YearRange) => void;
};

const validateYear = (year: string): boolean => {
  if (!year || year.trim() === "") return false;
  const yearNum = parseInt(year, 10);
  if (isNaN(yearNum)) return false;
  return yearNum >= 1900 && yearNum <= 2100;
};

const YearFilter = ({ yearFrom, yearTo, setYearRange }: YearFilterProps) => {
  const [fromValue, setFromValue] = useState(yearFrom);
  const [toValue, setToValue] = useState(yearTo);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    setFromValue(yearFrom);
    setToValue(yearTo);
  }, [yearFrom, yearTo]);

  const validate = (): boolean => {
    if (!validateYear(fromValue) || !validateYear(toValue)) return false;
    const from = parseInt(fromValue, 10);
    const to = parseInt(toValue, 10);
    return from <= to;
  };

  const handleSearch = () => {
    if (validate()) {
      setYearRange({ yearFrom: fromValue.trim(), yearTo: toValue.trim() });
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromValue(e.target.value);
    setIsValid(true);
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToValue(e.target.value);
    setIsValid(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <div className="relative">
          <CalendarDaysIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            id="year-from"
            value={fromValue}
            onChange={handleFromChange}
            onKeyDown={handleKeyDown}
            placeholder="From"
            aria-label="Year from"
            className={`h-10 w-[5.5rem] pl-9 pr-2.5 text-sm font-medium rounded-lg border bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0 focus:border-brand-500 transition-colors ${
              isValid ? "border-gray-300 hover:border-gray-400" : "border-red-500 focus:ring-red-500 focus:border-red-500"
            }`}
          />
        </div>
        <span className="text-gray-400 font-medium tabular-nums" aria-hidden="true">–</span>
        <div className="relative">
          <CalendarDaysIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            id="year-to"
            value={toValue}
            onChange={handleToChange}
            onKeyDown={handleKeyDown}
            placeholder="To"
            aria-label="Year to"
            className={`h-10 w-[5.5rem] pl-9 pr-2.5 text-sm font-medium rounded-lg border bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0 focus:border-brand-500 transition-colors ${
              isValid ? "border-gray-300 hover:border-gray-400" : "border-red-500 focus:ring-red-500 focus:border-red-500"
            }`}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={handleSearch}
        className="h-10 px-4 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg border border-transparent focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors"
      >
        Search
      </button>
    </div>
  );
};

export default YearFilter;
